import { api } from '../api/client';
import { correctScannedVat } from '../invoiceCorrections';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ADDONS,
  desgloseCuadra,
  totalDeFactura,
  type CostNature,
  type CatalogPricesProposal,
  type DuplicadoInfo,
  type ExpenseDraft,
  type ExpenseEntry,
  type ExpenseInput,
  type InvoiceStatus,
  type PaymentType,
  type PurchaseDocumentType,
  CORTESIA_SIN_LIMITE,
} from '@alexiarest/shared';
import { isDemoMode } from '../api/client';
import { usePeriod } from '../context/PeriodContext';
import { useSaveExpense, useSaveSupplier, useScanQuota, useScanReceipt, useSuppliers } from '../api/hooks';
import { formatDate, formatMoney, parseDecimal } from '../format';
import { CATEGORY_OPTIONS, DELIVERY_NOTE_LABEL, DELIVERY_NOTE_LABEL_LOWER } from '../labels';
import { Drawer } from './Drawer';
import { ACCEPT_ESCANER, FORMATOS_ESCANER, prepararImagen } from '../imagenEscaner';
import { formatDecimalInput } from '../format';
import { CheckboxField, FieldRow, SelectField, TextField } from './Field';
import { useToast } from './Toast';
import { InvoiceCatalogProposal } from './InvoiceCatalogProposal';

const OPCIONES_IMAGEN = {
  maxLado: 2000,
  maxPdfBytes: 8_000_000,
  errorPdf: 'Ese PDF pesa demasiado. Exporta solo las páginas de la factura (leemos hasta 5) o haz una foto.',
  errorTamanyo: 'La imagen es demasiado grande. Prueba a hacer la foto más de cerca.',
};

/** Marca para "crear un proveedor nuevo" en el select de proveedor. */
const NUEVO_PROVEEDOR = '__nuevo__';

/**
 * Escaner de facturas: de una foto a un gasto.
 *
 * La regla que ordena toda la pantalla: **esto propone, no guarda**. Lo que
 * devuelve el OCR aparece en un formulario editable, con los campos dudosos
 * marcados, y el gasto no entra en la cuenta de resultados hasta que alguien
 * pulsa Guardar. Un importe mal leido que se cuela solo descuadra el mes y
 * cuando se detecta ya nadie sabe de donde salio.
 */



export function ScanReceiptDrawer({
  open,
  onClose,
  documentType = 'factura',
  onGuardado,
  initialDraft,
  confirmExpense,
}: {
  open: boolean;
  onClose: () => void;
  documentType?: PurchaseDocumentType;
  /**
   * La factura recien guardada, para que la pantalla pueda proponer conciliarla
   * con los albaranes que estaban esperandola (9 sep 2026, Alex). El escaner no
   * decide nada: solo avisa de que hay una factura nueva.
   */
  onGuardado?: (expense: ExpenseEntry) => void;
  initialDraft?: { proposal: ExpenseDraft; key?: string };
  confirmExpense?: (input: ExpenseInput, acceptPossibleDuplicate?: boolean) => Promise<ExpenseEntry>;
}): JSX.Element | null {
  if (!open) return null;
  return <Cuerpo onClose={onClose} documentType={documentType} onGuardado={onGuardado} initialDraft={initialDraft} confirmExpense={confirmExpense} />;
}

function Cuerpo({
  onClose,
  documentType,
  onGuardado,
  initialDraft,
  confirmExpense,
}: {
  onClose: () => void;
  documentType: PurchaseDocumentType;
  onGuardado?: (expense: ExpenseEntry) => void;
  initialDraft?: { proposal: ExpenseDraft; key?: string };
  confirmExpense?: (input: ExpenseInput, acceptPossibleDuplicate?: boolean) => Promise<ExpenseEntry>;
}): JSX.Element {
  const toast = useToast();
  const quotaQuery = useScanQuota();
  const escanear = useScanReceipt();
  const qc = useQueryClient();
  const [draftId, setDraftId] = useState<string>();
  const guardar = useSaveExpense(async (input) => {
    if (confirmExpense) return confirmExpense(input, duplicadoAsumido);
    if (!draftId) return api.createExpense(input);
    const saved = await api.confirmDraft(draftId, { expense: input, acceptPossibleDuplicate: duplicadoAsumido });
    if (!saved.result?.expenseId) throw new Error('El gasto no se ha confirmado.');
    void qc.invalidateQueries({ queryKey: ['drafts'] });
    return api.getExpense(saved.result.expenseId);
  });
  const suppliersQuery = useSuppliers();
  const saveSupplier = useSaveSupplier();
  const { period } = usePeriod();

  const [nombreFichero, setNombreFichero] = useState('');
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<ExpenseDraft | null>(initialDraft?.proposal ?? null);
  const [catalogPrices, setCatalogPrices] = useState<CatalogPricesProposal>();
  const [catalogDraftId, setCatalogDraftId] = useState<string>();
  // Alta de proveedor al vuelo desde el escáner (tiene la factura delante).
  const [creandoProveedor, setCreandoProveedor] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState('');
  // Texto tal cual lo teclea el usuario: si el value fuera String(numero),
  // cada tecla se reformatearia y la coma decimal no entraria nunca.
  const [baseTxt, setBaseTxt] = useState(initialDraft ? formatDecimalInput(initialDraft.proposal.netAmount, 2) : '');
  const [vatTxt, setVatTxt] = useState(initialDraft ? ivaTexto(initialDraft.proposal) : '');
  const [subtotalTxt, setSubtotalTxt] = useState(initialDraft?.proposal.subtotalBeforeDiscount != null ? formatDecimalInput(initialDraft.proposal.subtotalBeforeDiscount, 2) : '');
  const [discountTxt, setDiscountTxt] = useState(initialDraft?.proposal.discountAmount != null ? formatDecimalInput(initialDraft.proposal.discountAmount, 2) : '');
  const [taxTxt, setTaxTxt] = useState(initialDraft?.proposal.taxAmount != null ? formatDecimalInput(initialDraft.proposal.taxAmount, 2) : '');
  const [totalTxt, setTotalTxt] = useState(initialDraft ? formatDecimalInput(totalDeFactura(initialDraft.proposal), 2) : '');
  const [key, setKey] = useState<string | undefined>(initialDraft?.key);
  // Duplicado: hay que decidir a mano. Empieza siempre en false, tambien al
  // escanear otra factura, para que un "guardar igualmente" no se herede.
  const [duplicadoAsumido, setDuplicadoAsumido] = useState(false);

  const quota = quotaQuery.data;
  const bloqueado = initialDraft ? false : quota ? quota.sinModulo || quota.exhausted : false;

  const resetProveedor = () => {
    setCreandoProveedor(false);
    setNuevoProveedor('');
  };

  const cargar = async (file: File) => {
    setError('');
    setDraft(null);
    setDuplicadoAsumido(false);
    resetProveedor();
    setNombreFichero(file.name);
    try {
      const blob = await prepararImagen(file, OPCIONES_IMAGEN);
      const res = await escanear.mutateAsync({ blob, documentType });
      setDraft(documentType === 'albaran' ? { ...res.draft, paid: false } : res.draft);
      setBaseTxt(res.draft.netAmount ? formatDecimalInput(res.draft.netAmount, 2) : '');
      setVatTxt(ivaTexto(res.draft));
      setSubtotalTxt(res.draft.subtotalBeforeDiscount != null ? formatDecimalInput(res.draft.subtotalBeforeDiscount, 2) : '');
      setDiscountTxt(res.draft.discountAmount != null ? formatDecimalInput(res.draft.discountAmount, 2) : '');
      setTaxTxt(res.draft.taxAmount != null ? formatDecimalInput(res.draft.taxAmount, 2) : '');
      setTotalTxt(formatDecimalInput(totalDeFactura(res.draft), 2));
      setKey(res.key);
      setDraftId(res.draftId);
      setCatalogPrices(res.catalogPrices);
      setCatalogDraftId(res.catalogDraftId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No hemos podido leer el documento.');
    }
  };

  const probarEjemplo = async (dosPaginas = false) => {
    setError('');
    setDuplicadoAsumido(false);
    resetProveedor();
    setNombreFichero(dosPaginas ? 'factura-de-2-paginas.pdf' : 'factura-de-ejemplo.jpg');
    try {
      const res = await escanear.mutateAsync({ blob: new Blob([], { type: dosPaginas ? 'application/pdf' : 'image/jpeg' }), documentType });
      setDraft(documentType === 'albaran' ? { ...res.draft, paid: false } : res.draft);
      setBaseTxt(res.draft.netAmount ? formatDecimalInput(res.draft.netAmount, 2) : '');
      setVatTxt(ivaTexto(res.draft));
      setSubtotalTxt(res.draft.subtotalBeforeDiscount != null ? formatDecimalInput(res.draft.subtotalBeforeDiscount, 2) : '');
      setDiscountTxt(res.draft.discountAmount != null ? formatDecimalInput(res.draft.discountAmount, 2) : '');
      setTaxTxt(res.draft.taxAmount != null ? formatDecimalInput(res.draft.taxAmount, 2) : '');
      setTotalTxt(formatDecimalInput(totalDeFactura(res.draft), 2));
      setKey(res.key);
      setDraftId(res.draftId);
      setCatalogPrices(res.catalogPrices);
      setCatalogDraftId(res.catalogDraftId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No hemos podido leer el documento.');
    }
  };

  const campo = <K extends keyof ExpenseDraft>(k: K, v: ExpenseDraft[K]) =>
    setDraft((prev) => (prev ? { ...prev, [k]: v } : prev));

  const crearProveedor = (nombreInicial?: string) => {
    const nombre = (nombreInicial ?? nuevoProveedor).trim();
    if (!nombre) return;
    saveSupplier.mutate(
      { input: { name: nombre } },
      {
        onSuccess: (creado) => {
          // Queda enganchado al gasto y desaparece el aviso de "no está en tus
          // proveedores".
          setDraft((prev) =>
            prev ? { ...prev, supplierId: creado.supplierId, supplierName: creado.name } : prev,
          );
          resetProveedor();
          toast.success(`Proveedor "${creado.name}" creado.`);
        },
        onError: (error) => toast.fail(error),
      },
    );
  };

  /** Cambiar el tipo de pago mantiene coherentes vencimiento y estado. */
  const cambiarTipoPago = (tipo: PaymentType) => {
    setDraft((prev) => {
      if (!prev) return prev;
      if (tipo === 'contado') {
        // Contado = pagado y sin vencimiento (como en el alta manual).
        return { ...prev, paymentType: 'contado', dueDate: undefined, paid: true };
      }
      return { ...prev, paymentType: 'credito', paid: false };
    });
  };

  const cambiarBase = (texto: string) => {
    setBaseTxt(texto);
    const netAmount = parseDecimal(texto);
    setDraft((prev) => {
      if (!prev) return prev;
      const invoiceTotal = prev.taxAmount != null ? Math.round((netAmount + prev.taxAmount) * 100) / 100 : prev.invoiceTotal;
      const discountAmount = prev.subtotalBeforeDiscount != null
        ? Math.max(0, Math.round((prev.subtotalBeforeDiscount - netAmount) * 100) / 100)
        : prev.discountAmount;
      const vatPct = prev.taxAmount != null && netAmount > 0
        ? Math.round((prev.taxAmount / netAmount) * 1_000_000) / 10_000
        : prev.vatPct;
      if (invoiceTotal != null) setTotalTxt(formatDecimalInput(invoiceTotal, 2));
      if (discountAmount != null) setDiscountTxt(formatDecimalInput(discountAmount, 2));
      return { ...prev, netAmount, discountAmount, vatPct, invoiceTotal, vatBreakdown: undefined, vatNote: notaImpuestoMixto(prev.taxMode, prev.taxAmount) };
    });
  };

  const cambiarSubtotal = (texto: string) => {
    setSubtotalTxt(texto);
    const subtotalBeforeDiscount = texto.trim() ? parseDecimal(texto) : undefined;
    setDraft((prev) => {
      if (!prev) return prev;
      if (subtotalBeforeDiscount == null || prev.discountAmount == null) return { ...prev, subtotalBeforeDiscount };
      const netAmount = Math.round((subtotalBeforeDiscount - prev.discountAmount) * 100) / 100;
      const invoiceTotal = prev.taxAmount != null ? Math.round((netAmount + prev.taxAmount) * 100) / 100 : prev.invoiceTotal;
      setBaseTxt(formatDecimalInput(netAmount, 2));
      if (invoiceTotal != null) setTotalTxt(formatDecimalInput(invoiceTotal, 2));
      return { ...prev, subtotalBeforeDiscount, netAmount, invoiceTotal, vatBreakdown: undefined, vatNote: notaImpuestoMixto(prev.taxMode, prev.taxAmount) };
    });
  };

  const cambiarDescuento = (texto: string) => {
    setDiscountTxt(texto);
    const discountAmount = texto.trim() ? Math.abs(parseDecimal(texto)) : undefined;
    setDraft((prev) => {
      if (!prev) return prev;
      if (discountAmount == null || prev.subtotalBeforeDiscount == null) return { ...prev, discountAmount };
      const netAmount = Math.round((prev.subtotalBeforeDiscount - discountAmount) * 100) / 100;
      const invoiceTotal = prev.taxAmount != null ? Math.round((netAmount + prev.taxAmount) * 100) / 100 : prev.invoiceTotal;
      setBaseTxt(formatDecimalInput(netAmount, 2));
      if (invoiceTotal != null) setTotalTxt(formatDecimalInput(invoiceTotal, 2));
      return { ...prev, discountAmount, netAmount, invoiceTotal, vatBreakdown: undefined, vatNote: notaImpuestoMixto(prev.taxMode, prev.taxAmount) };
    });
  };

  const cambiarImpuesto = (texto: string) => {
    setTaxTxt(texto);
    const taxAmount = texto.trim() ? parseDecimal(texto) : undefined;
    setDraft((prev) => {
      if (!prev) return prev;
      const vatPct = taxAmount != null && prev.netAmount > 0
        ? Math.round((taxAmount / prev.netAmount) * 1_000_000) / 10_000
        : prev.vatPct;
      const invoiceTotal = taxAmount != null ? Math.round((prev.netAmount + taxAmount) * 100) / 100 : prev.invoiceTotal;
      if (invoiceTotal != null) setTotalTxt(formatDecimalInput(invoiceTotal, 2));
      return { ...prev, taxAmount, vatAmount: taxAmount, vatPct, invoiceTotal, vatBreakdown: undefined, vatNote: notaImpuestoMixto(prev.taxMode, taxAmount) };
    });
  };

  const cambiarTotal = (texto: string) => {
    setTotalTxt(texto);
    const invoiceTotal = texto.trim() ? parseDecimal(texto) : undefined;
    setDraft((prev) => {
      if (!prev) return prev;
      if (invoiceTotal == null || prev.taxAmount == null) return { ...prev, invoiceTotal, totalLeido: invoiceTotal };
      const netAmount = Math.round((invoiceTotal - prev.taxAmount) * 100) / 100;
      const discountAmount = prev.subtotalBeforeDiscount != null
        ? Math.max(0, Math.round((prev.subtotalBeforeDiscount - netAmount) * 100) / 100)
        : prev.discountAmount;
      const vatPct = netAmount > 0 ? Math.round((prev.taxAmount / netAmount) * 1_000_000) / 10_000 : prev.vatPct;
      setBaseTxt(formatDecimalInput(netAmount, 2));
      if (discountAmount != null) setDiscountTxt(formatDecimalInput(discountAmount, 2));
      return { ...prev, invoiceTotal, totalLeido: invoiceTotal, netAmount, discountAmount, vatPct, vatBreakdown: undefined, vatNote: notaImpuestoMixto(prev.taxMode, prev.taxAmount) };
    });
  };

  /**
   * Corregir el IVA recalcula la base a partir del TOTAL leido en el
   * documento. Es el arreglo del ticket de Pol: si el desglose salio con un
   * tipo equivocado, cambiar el tipo lo endereza todo — sin hacer la division
   * a mano. Si no hay total leido, el IVA cambia y la base se queda.
   */
  const cambiarIva = (texto: string) => {
    setVatTxt(texto);
    /*
     * Vaciar la casilla para teclear otro numero NO es "IVA 0 %".
     *
     * Lo era: `parseDecimal('')` devuelve 0, y con total/(1+0) la base pasaba
     * a ser el TOTAL CON IVA. Alex escaneo una factura que se habia leido bien
     * (base 171,30) y acabo guardada como 188,43 al 0 %. Mientras la casilla
     * este vacia no se toca nada; el borrador conserva el ultimo tipo valido.
     */
    if (texto.trim() === '') return;
    // «Varios» (o a medio borrar) no es un numero: tampoco se toca nada.
    if (!/\d/.test(texto)) return;
    const vat = parseDecimal(texto);
    setDraft((prev) => {
      if (!prev) return prev;
      const next = correctScannedVat(prev, vat);
      setBaseTxt(formatDecimalInput(next.netAmount, 2));
      setTaxTxt(next.taxAmount == null ? '' : formatDecimalInput(next.taxAmount, 2));
      setTotalTxt(next.invoiceTotal == null ? '' : formatDecimalInput(next.invoiceTotal, 2));
      if (next.subtotalBeforeDiscount != null) setSubtotalTxt(formatDecimalInput(next.subtotalBeforeDiscount, 2));
      return next;
    });
  };

  // Con un duplicado sin resolver, Guardar esta apagado. No es un aviso mas:
  // guardar dos veces la misma factura descuadra el mes y las cuentas por
  // pagar, y se detecta semanas despues.
  const bloqueadoPorDuplicado = Boolean(draft?.duplicado) && !duplicadoAsumido;

  const confirmar = () => {
    if (!draft) return;
    if (bloqueadoPorDuplicado) return;
    if (draft.netAmount <= 0) {
      toast.notify('Escribe el importe antes de guardar.', 'error');
      return;
    }
    const input: ExpenseInput = {
      documentType,
      date: draft.date,
      dueDate: draft.dueDate,
      concept: draft.concept,
      supplierId: draft.supplierId,
      invoiceNumber: draft.invoiceNumber,
      category: draft.category,
      nature: draft.nature,
      netAmount: draft.netAmount,
      vatPct: draft.vatPct,
      subtotalBeforeDiscount: draft.subtotalBeforeDiscount,
      discountAmount: draft.discountAmount,
      taxAmount: draft.taxAmount,
      invoiceTotal: draft.invoiceTotal,
      taxMode: draft.taxMode,
      // Si se ha corregido la base a mano, el cuadro leido ya no vale.
      vatBreakdown: desgloseCuadra(draft.vatBreakdown, draft.netAmount) ? draft.vatBreakdown : undefined,
      paymentType: draft.paymentType,
      invoiceStatus: documentType === 'albaran' ? 'pendiente' : draft.invoiceStatus,
      paid: draft.paid,
      // El documento queda enganchado al gasto: es el justificante.
      attachmentKey: key && !key.startsWith('demo/') ? key : undefined,
      // Con varios tipos de IVA el gasto guarda el tipo efectivo; el desglose
      // de verdad queda escrito en las notas, que es lo que lee la gestoria.
      notes: draft.vatNote,
    };
    guardar.mutate(
      { input },
      {
        onSuccess: (guardado) => {
          // Una factura de agosto guardada mientras miras julio se guarda
          // bien pero no aparece en la tabla, y parece que se ha perdido.
          const fuera = draft.date < period.start || draft.date > period.end;
          toast.success(
            fuera
              ? `Gasto guardado con fecha ${formatDate(draft.date)}. No lo ves en la tabla porque está fuera del periodo que tienes seleccionado.`
              : documentType === 'albaran' ? `${DELIVERY_NOTE_LABEL} guardada como compra provisional.` : 'Gasto guardado desde la factura.',
          );
          onClose();
          if (documentType !== 'albaran') onGuardado?.(guardado);
        },
        onError: (e) => toast.fail(e),
      },
    );
  };

  const total = draft ? totalDeFactura(draft) : 0;

  return (
    <Drawer
      open
      title={documentType === 'albaran' ? `Escanear una ${DELIVERY_NOTE_LABEL_LOWER}` : 'Escanear una factura'}
      subtitle={documentType === 'albaran'
        ? 'Quedará pendiente de factura y no entrará como gasto definitivo hasta conciliarla.'
        : 'Sube una foto o PDF y Alexia extraerá los datos para proponerte el gasto. Nada se guardará hasta que lo confirmes.'}
      onClose={onClose}
      width="42rem"
      footer={
        draft ? (
          <>
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn"
              disabled={guardar.isPending || bloqueadoPorDuplicado}
              onClick={confirmar}
            >
              {guardar.isPending
                ? 'Guardando…'
                : draft.duplicado && duplicadoAsumido
                  ? 'Guardar de todos modos'
                  : 'Guardar gasto'}
            </button>
          </>
        ) : undefined
      }
    >
      <div className="stack">
        {quota && bloqueado && (
          <p className="alerta">
            {quota.message ??
              `El escáner es un módulo aparte: ${ADDONS.escaner.pricePerMonth} € al mes.`}
          </p>
        )}

        {quota && !bloqueado && (
          <p className="field__hint">
            {quota.extension ? 'Páginas disponibles en la extensión: ' : 'Escaneos disponibles este mes: '}<strong>{quota.remaining}</strong>
            {quota.limit && quota.limit < CORTESIA_SIN_LIMITE ? ` de ${quota.limit}` : ''}
          </p>
        )}

        {!bloqueado && (
          <div className="row">
            <label className="btn btn--ghost">
              {escanear.isPending ? 'Leyendo…' : 'Elegir foto o PDF'}
              <input
                type="file"
                accept={ACCEPT_ESCANER}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void cargar(file);
                  e.target.value = '';
                }}
              />
            </label>
            {isDemoMode && (
              <button type="button" className="btn btn--ghost" onClick={() => void probarEjemplo()}>
                Probar con una factura de ejemplo
              </button>
            )}
            {isDemoMode && (
              <button type="button" className="btn btn--ghost" onClick={() => void probarEjemplo(true)}>
                Probar con un PDF de 2 páginas
              </button>
            )}
            {nombreFichero && <span className="field__hint">{nombreFichero}</span>}
          </div>
        )}
        {!bloqueado && <p className="field__hint">{FORMATOS_ESCANER}</p>}

        {error && <p className="alerta alerta--error">{error}</p>}

        {draft && (
          <>
            {draft.duplicado && (
              <DuplicadoAviso
                info={draft.duplicado}
                asumido={duplicadoAsumido}
                onAsumir={setDuplicadoAsumido}
                onDescartar={onClose}
              />
            )}

            {draft.avisos.length > 0 && (
              <div className="form__section">
                <p className="form__section-title">Compruébalo antes de guardar</p>
                <ul className="lista-problemas">
                  {draft.avisos.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            )}

            <form className="form" onSubmit={(e) => e.preventDefault()}>
              <TextField
                label="Concepto"
                value={draft.concept}
                onChange={(v) => campo('concept', v)}
              />

              {/* Proveedor: elegir uno existente o crearlo al vuelo. */}
              <SelectField
                label="Proveedor"
                value={draft.supplierId ?? ''}
                onChange={(value) => {
                  if (value === NUEVO_PROVEEDOR) {
                    setCreandoProveedor(true);
                    setNuevoProveedor(draft.supplierName ?? '');
                    return;
                  }
                  setCreandoProveedor(false);
                  campo('supplierId', value || undefined);
                }}
                options={[
                  { value: '', label: 'Sin proveedor asignado' },
                  ...(suppliersQuery.data ?? []).map((s) => ({ value: s.supplierId, label: s.name })),
                  { value: NUEVO_PROVEEDOR, label: '➕ Crear un proveedor nuevo…', fija: true },
                ]}
                hint={
                  !draft.supplierId && draft.supplierName && !creandoProveedor
                    ? `Detectamos “${draft.supplierName}” en la factura, pero no está registrado como proveedor.`
                    : undefined
                }
              />
              {!draft.supplierId && draft.supplierName && !creandoProveedor && (
                <div className="row">
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={saveSupplier.isPending}
                    onClick={() => crearProveedor(draft.supplierName)}
                  >
                    {saveSupplier.isPending ? 'Creando…' : `Crear «${draft.supplierName}» como proveedor`}
                  </button>
                </div>
              )}
              {creandoProveedor && (
                <div className="form__inline">
                  <TextField
                    label="Nombre del proveedor nuevo"
                    value={nuevoProveedor}
                    onChange={setNuevoProveedor}
                    placeholder="Distribuciones del Norte"
                    hint="Se crea al momento y queda enganchado a este gasto."
                  />
                  <div className="row" style={{ alignItems: 'flex-end', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => crearProveedor()}
                      disabled={saveSupplier.isPending || nuevoProveedor.trim() === ''}
                    >
                      {saveSupplier.isPending ? 'Creando…' : 'Crear'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={resetProveedor}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
              <FieldRow>
                <TextField
                  label="Fecha"
                  type="date"
                  value={draft.date}
                  onChange={(v) => campo('date', v)}
                  error={draft.confianza.fecha === 'baja' ? 'Compruébala' : undefined}
                />
                <TextField
                  label={documentType === 'albaran' ? 'Nº de documento' : 'Nº de factura / folio / referencia'}
                  value={draft.invoiceNumber ?? ''}
                  onChange={(v) => campo('invoiceNumber', v || undefined)}
                />
              </FieldRow>
              <FieldRow>
                <TextField
                  label="Subtotal antes de descuentos"
                  value={subtotalTxt}
                  onChange={cambiarSubtotal}
                  inputMode="decimal"
                />
                <TextField
                  label="Descuento total"
                  value={discountTxt}
                  onChange={cambiarDescuento}
                  inputMode="decimal"
                />
              </FieldRow>
              <FieldRow>
                <TextField
                  label="Importe antes de impuestos"
                  value={baseTxt}
                  onChange={cambiarBase}
                  inputMode="decimal"
                  error={draft.confianza.importe === 'baja' ? 'No lo hemos leído bien' : undefined}
                />
                <TextField label="Impuestos" value={taxTxt} onChange={cambiarImpuesto} inputMode="decimal" />
              </FieldRow>
              <FieldRow>
                <SelectField
                  label="Tipo de impuesto"
                  value={draft.taxMode ?? 'single'}
                  onChange={(value) => {
                    const taxMode = value as 'single' | 'mixed';
                    setDraft((prev) => prev ? {
                      ...prev,
                      taxMode,
                      ...(taxMode === 'single'
                        ? { vatBreakdown: undefined, vatNote: undefined }
                        : { vatNote: notaImpuestoMixto(taxMode, prev.taxAmount) }),
                    } : prev);
                    if (taxMode === 'mixed') setVatTxt('Varios');
                    else setVatTxt(formatDecimalInput(draft.vatPct, 2));
                  }}
                  options={[{ value: 'single', label: 'Un solo tipo' }, { value: 'mixed', label: 'Mixto / varios tipos' }]}
                />
                <TextField
                  label="IVA de la compra (%)"
                  value={vatTxt}
                  onChange={cambiarIva}
                  inputMode="decimal"
                  hint={
                    draft.taxMode === 'mixed' || draft.vatBreakdown
                      ? `${draft.vatNote ? `${draft.vatNote} ` : ''}Se guarda el importe real del impuesto; no se fuerza un porcentaje único.`
                      : draft.totalLeido != null ? 'Al cambiar el IVA, el importe antes de impuestos se recalcula a partir del total.' : undefined
                  }
                />
              </FieldRow>
              <FieldRow>
                <TextField label="Total de la factura" value={totalTxt} onChange={cambiarTotal} inputMode="decimal" />
                <div />
              </FieldRow>
              <FieldRow>
                <SelectField
                  label="Categoría"
                  value={draft.category}
                  onChange={(v) => campo('category', v as ExpenseDraft['category'])}
                  options={CATEGORY_OPTIONS}
                  hint="Alexia ha sugerido esta categoría según el proveedor. Cámbiala si no corresponde."
                />
                <SelectField
                  label="Tipo de coste"
                  value={draft.nature}
                  onChange={(v) => campo('nature', v as CostNature)}
                  options={[
                    { value: 'fijo', label: 'Fijo' },
                    { value: 'variable', label: 'Variable' },
                  ]}
                  hint="¿Es un coste fijo o cambia según la actividad del negocio?"
                />
              </FieldRow>

              {/* Cómo se paga y en qué estado (antes se deducía y no se veía). */}
              <FieldRow>
                <SelectField
                  label="Condición de pago"
                  value={draft.paymentType}
                  onChange={(v) => cambiarTipoPago(v as PaymentType)}
                  options={[
                    { value: 'contado', label: 'Contado' },
                    { value: 'credito', label: 'Crédito' },
                  ]}
                />
                <TextField
                  label="Vencimiento"
                  type="date"
                  value={draft.dueDate ?? ''}
                  onChange={(v) => campo('dueDate', v || undefined)}
                  disabled={draft.paymentType !== 'credito'}
                  hint={draft.paymentType !== 'credito' ? 'Solo a crédito.' : undefined}
                />
              </FieldRow>
              <FieldRow>
                <SelectField
                  label="Estado de la factura"
                  value={draft.invoiceStatus}
                  onChange={(v) => campo('invoiceStatus', v as InvoiceStatus)}
                  options={[
                    { value: 'facturado', label: 'Factura recibida' },
                    { value: 'pendiente', label: 'Pendiente de factura' },
                    { value: 'sin_factura', label: 'Sin factura' },
                  ]}
                />
                <CheckboxField
                  label={documentType === 'albaran' ? 'Ya la pagué al recibirla' : 'Ya está pagado'}
                  checked={draft.paid}
                  onChange={(v) => campo('paid', v)}
                />
              </FieldRow>
              <p className="form__section-hint">
                Total con IVA: <strong>{formatMoney(total)}</strong>
                {draft.totalLeido != null && Math.abs(total - draft.totalLeido) > 0.02 && (
                  <> · en la factura pone {formatMoney(draft.totalLeido)}</>
                )}
                {draft.paymentType === 'credito' && draft.dueDate && (
                  <> · a crédito, vence el {draft.dueDate}</>
                )}
              </p>
            </form>
            {catalogPrices && <InvoiceCatalogProposal key={nombreFichero} proposal={catalogPrices} />}
          </>
        )}
      </div>
    </Drawer>
  );
}

/**
 * Aviso de factura repetida.
 *
 * Deliberadamente NO es una linea mas en la lista de avisos: ensena el gasto
 * con el que choca —fecha, concepto, numero e importe— para que se pueda
 * comparar sin salir de aqui, y **apaga el boton de guardar** hasta que
 * alguien decide. Guardar dos veces la misma factura descuadra el mes y las
 * cuentas por pagar, y no se detecta hasta semanas despues, cuando ya nadie
 * recuerda que paso.
 *
 * Las dos salidas son igual de faciles: descartar (que es lo que toca casi
 * siempre) o guardarlo igualmente marcando la casilla.
 */
function DuplicadoAviso({
  info,
  asumido,
  onAsumir,
  onDescartar,
}: {
  info: DuplicadoInfo;
  asumido: boolean;
  onAsumir: (v: boolean) => void;
  onDescartar: () => void;
}): JSX.Element {
  const total = totalDeFactura(info.existente);
  const seguro = info.certeza === 'segura';

  return (
    <div className={`alerta ${seguro ? 'alerta--error' : ''}`.trim()}>
      <p>
        <strong>Parece que esta factura ya está registrada.</strong>{' '}
        {info.explicacion}
      </p>

      <div className="duplicado__ficha">
        <span className="duplicado__etiqueta">FACTURA YA REGISTRADA</span>
        <div>
          <strong>{info.existente.concept}</strong>
          {info.existente.invoiceNumber && <> · {info.existente.invoiceNumber}</>}
        </div>
        <div className="cell-sub">
          {formatDate(info.existente.date)} · {formatMoney(total)} con IVA
        </div>
      </div>

      <div className="row">
        <button type="button" className="btn btn--sm" onClick={onDescartar}>
          Descartar este escaneo
        </button>
        <label className="duplicado__confirmar">
          <input type="checkbox" checked={asumido} onChange={(e) => onAsumir(e.target.checked)} />
          No es la misma factura
        </label>
      </div>
    </div>
  );
}

/** Lo que se ensena en la casilla del IVA: con varios tipos, «Varios», nunca el tipo efectivo. */
function ivaTexto(d: Pick<ExpenseDraft, 'vatPct' | 'vatBreakdown' | 'taxMode'>): string {
  return d.taxMode === 'mixed' || d.vatBreakdown ? 'Varios' : formatDecimalInput(d.vatPct, 2);
}

function notaImpuestoMixto(mode: ExpenseDraft['taxMode'], amount: number | undefined): string | undefined {
  return mode === 'mixed' && amount != null
    ? `Impuesto mixto / varios tipos. Cuota total: ${formatDecimalInput(amount, 2)}.`
    : undefined;
}
